import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { IoImageOutline, IoCloseOutline } from 'react-icons/io5';
import { MdDragIndicator } from 'react-icons/md';

const STATUS_LABEL = { VALID: 'Aktiv', UPCOMING: 'Kommande', EXPIRED: 'Utgången' };

function PostThumb({ url }) {
  return url ? (
    <img className="series-post-thumb" alt="" src={url} />
  ) : (
    <span className="series-post-thumb series-post-thumb--placeholder">
      <IoImageOutline size={22} color="#c4c4c4" />
    </span>
  );
}

PostThumb.propTypes = { url: PropTypes.string };

function SortablePostRow({ post, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: post._id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : undefined,
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={`series-post-row${post.validityStatus === 'EXPIRED' ? ' is-expired' : ''}`}
    >
      <button type="button" className="drag-handle" {...attributes} {...listeners} aria-label="Dra för att ändra ordning">
        <MdDragIndicator size={20} />
      </button>
      <PostThumb url={post.imageFullURL} />
      <div className="series-post-info">
        <span className="series-post-title">
          {post.title || <em style={{ color: 'rgba(0,0,0,0.4)' }}>(ingen titel)</em>}
        </span>
        <span className="series-post-status">
          {STATUS_LABEL[post.validityStatus] || post.validityStatus}
        </span>
      </div>
      <button type="button" className="icon-btn icon-btn--danger" onClick={() => onRemove(post._id)} aria-label="Ta bort">
        <IoCloseOutline size={20} />
      </button>
    </li>
  );
}

SortablePostRow.propTypes = {
  post: PropTypes.object.isRequired,
  onRemove: PropTypes.func.isRequired,
};

function ReorderablePostList({ posts, onReorder, onRemove }) {
  const [localPosts, setLocalPosts] = useState(posts);
  useEffect(() => { setLocalPosts(posts); }, [posts]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = localPosts.findIndex((p) => p._id === active.id);
    const newIndex = localPosts.findIndex((p) => p._id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(localPosts, oldIndex, newIndex);
    setLocalPosts(reordered);
    onReorder(reordered.map((p) => p._id));
  };

  if (localPosts.length === 0) {
    return <p className="series-posts-empty">Inga inlägg i serien än.</p>;
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={localPosts.map((p) => p._id)} strategy={verticalListSortingStrategy}>
        <ul className="series-posts-list">
          {localPosts.map((post) => (
            <SortablePostRow key={post._id} post={post} onRemove={onRemove} />
          ))}
        </ul>
      </SortableContext>
    </DndContext>
  );
}

ReorderablePostList.propTypes = {
  posts: PropTypes.array.isRequired,
  onReorder: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
};

export default ReorderablePostList;
